// Tiny pooled particle system. Shapes are drawn once (in white, into shared GraphicsContexts)
// and tinted per particle, so a burst only flips transforms on preallocated Graphics.
// Studio Mode: setEnabled(false) turns bursts into no-ops and hides live particles.
import { Container, Graphics, GraphicsContext } from "pixi.js";
import { INK } from "../palette";
import { mulberry32 } from "../lib/rng";

export type ParticleKind = "star" | "confetti" | "note" | "bubble" | "spark";

const KINDS: ParticleKind[] = ["star", "confetti", "note", "bubble", "spark"];

interface Particle {
  g: Graphics;
  kind: ParticleKind;
  active: boolean;
  vx: number;
  vy: number;
  vr: number;
  life: number;
  maxLife: number;
  baseScale: number;
  seed: number;
}

function makeContext(kind: ParticleKind, u: number): GraphicsContext {
  const c = new GraphicsContext();
  const ow = Math.max(1, u * 0.14);
  switch (kind) {
    case "star":
      c.star(0, 0, 5, u, u * 0.45)
        .fill({ color: 0xffffff })
        .stroke({ width: ow, color: INK, join: "round" });
      break;
    case "confetti":
      c.roundRect(-u * 0.45, -u * 0.22, u * 0.9, u * 0.44, u * 0.08).fill({ color: 0xffffff });
      break;
    case "note":
      // Eighth note: head, stem, flag.
      c.moveTo(u * 0.3, u * 0.35)
        .lineTo(u * 0.3, -u * 0.8)
        .quadraticCurveTo(u * 0.75, -u * 0.55, u * 0.7, -u * 0.15)
        .stroke({ width: ow * 1.6, color: 0xffffff, cap: "round", join: "round" });
      c.ellipse(0, u * 0.4, u * 0.38, u * 0.28)
        .fill({ color: 0xffffff })
        .stroke({ width: ow, color: INK });
      break;
    case "bubble":
      c.circle(0, 0, u * 0.6)
        .fill({ color: 0xffffff, alpha: 0.25 })
        .stroke({ width: ow, color: 0xffffff });
      c.circle(-u * 0.22, -u * 0.22, u * 0.13).fill({ color: 0xffffff });
      break;
    case "spark":
      c.poly([-u * 0.9, 0, 0, -u * 0.18, u * 0.9, 0, 0, u * 0.18]).fill({ color: 0xffffff });
      break;
  }
  return c;
}

export class ParticleBurst extends Container {
  private readonly pool: Particle[] = [];
  private readonly contexts = new Map<ParticleKind, GraphicsContext>();
  private readonly rand: () => number;
  private next = 0;
  private enabledFlag = true;
  private live = 0;

  /**
   * @param capacity max simultaneous particles (preallocated)
   * @param unit base particle size in px
   */
  constructor(capacity = 256, unit = 8, seed = 0x5eed) {
    super();
    this.rand = mulberry32(seed);
    for (const k of KINDS) this.contexts.set(k, makeContext(k, unit));
    const ctx0 = this.contexts.get("star")!;
    for (let i = 0; i < capacity; i++) {
      const g = new Graphics(ctx0);
      g.visible = false;
      this.addChild(g);
      this.pool.push({
        g,
        kind: "star",
        active: false,
        vx: 0,
        vy: 0,
        vr: 0,
        life: 0,
        maxLife: 1,
        baseScale: 1,
        seed: 0,
      });
    }
  }

  get enabled(): boolean {
    return this.enabledFlag;
  }

  /** Studio Mode / reduced motion: disable to suppress all particles. */
  setEnabled(on: boolean): void {
    this.enabledFlag = on;
    if (!on) {
      for (const p of this.pool) {
        p.active = false;
        p.g.visible = false;
      }
      this.live = 0;
    }
  }

  /** Number of live particles (for the debug overlay). */
  get activeCount(): number {
    return this.live;
  }

  /** Emit `count` particles of `kind` at (x, y) in this container's space. Recycles the oldest when full. */
  burst(x: number, y: number, color: number, count: number, kind: ParticleKind): void {
    if (!this.enabledFlag || this.pool.length === 0) return;
    const ctx = this.contexts.get(kind)!;
    const R = this.rand;
    for (let n = 0; n < count; n++) {
      const p = this.pool[this.next];
      this.next = (this.next + 1) % this.pool.length;
      if (!p.active) this.live++;
      p.active = true;
      p.kind = kind;
      if (p.g.context !== ctx) p.g.context = ctx;
      p.g.tint = kind === "bubble" ? 0xffffff : color;
      p.g.visible = true;
      p.g.position.set(x, y);
      p.g.alpha = 1;
      p.seed = R() * 6.283;
      const a = R() * Math.PI * 2;
      switch (kind) {
        case "star": {
          const sp = 80 + R() * 140;
          p.vx = Math.cos(a) * sp;
          p.vy = Math.sin(a) * sp - 80;
          p.vr = (R() - 0.5) * 10;
          p.maxLife = 0.6 + R() * 0.35;
          p.baseScale = 0.6 + R() * 0.6;
          break;
        }
        case "confetti":
          p.vx = (R() - 0.5) * 300;
          p.vy = -150 - R() * 220;
          p.vr = (R() - 0.5) * 20;
          p.maxLife = 1 + R() * 0.5;
          p.baseScale = 0.7 + R() * 0.5;
          // Confetti picks varied tints around the given color.
          if (n % 3 === 1) p.g.tint = 0xf0e442;
          else if (n % 3 === 2) p.g.tint = 0x56b4e9;
          break;
        case "note":
          p.vx = (R() - 0.5) * 60;
          p.vy = -50 - R() * 50;
          p.vr = (R() - 0.5) * 1.5;
          p.maxLife = 1 + R() * 0.5;
          p.baseScale = 0.8 + R() * 0.5;
          break;
        case "bubble":
          p.vx = (R() - 0.5) * 30;
          p.vy = -35 - R() * 45;
          p.vr = 0;
          p.maxLife = 1.2 + R() * 0.8;
          p.baseScale = 0.5 + R() * 0.9;
          break;
        case "spark": {
          const sp = 200 + R() * 180;
          p.vx = Math.cos(a) * sp;
          p.vy = Math.sin(a) * sp;
          p.vr = 0;
          p.maxLife = 0.25 + R() * 0.25;
          p.baseScale = 0.6 + R() * 0.5;
          break;
        }
      }
      p.life = p.maxLife;
      p.g.rotation = kind === "spark" ? Math.atan2(p.vy, p.vx) : R() * 6.283;
      p.g.scale.set(p.baseScale, p.baseScale);
    }
  }

  /** Advance the simulation by dt seconds. */
  update(dt: number): void {
    if (this.live === 0) return;
    for (let i = 0; i < this.pool.length; i++) {
      const p = this.pool[i];
      if (!p.active) continue;
      p.life -= dt;
      const g = p.g;
      if (p.life <= 0) {
        p.active = false;
        g.visible = false;
        this.live--;
        continue;
      }
      const k = p.life / p.maxLife; // 1 → 0
      let gravity = 0,
        drag = 0;
      switch (p.kind) {
        case "star":
          gravity = 380;
          drag = 1.2;
          break;
        case "confetti":
          gravity = 420;
          drag = 2.5;
          break;
        case "note":
          gravity = -10;
          drag = 0.5;
          p.vx += Math.sin(p.life * 6 + p.seed) * 40 * dt;
          break;
        case "bubble":
          gravity = -20;
          drag = 0.3;
          p.vx += Math.sin(p.life * 5 + p.seed) * 50 * dt;
          break;
        case "spark":
          gravity = 0;
          drag = 6;
          break;
      }
      p.vy += gravity * dt;
      const f = Math.max(0, 1 - drag * dt);
      p.vx *= f;
      p.vy *= f;
      g.x += p.vx * dt;
      g.y += p.vy * dt;
      g.rotation += p.vr * dt;
      const s = p.baseScale;
      switch (p.kind) {
        case "confetti":
          g.scale.set(s * Math.cos(p.life * 12 + p.seed), s); // flutter
          g.alpha = Math.min(1, k * 3);
          break;
        case "bubble":
          // Pop: swell in the last 10% of life.
          g.scale.set(k < 0.1 ? s * (1 + (0.1 - k) * 6) : s, k < 0.1 ? s * (1 + (0.1 - k) * 6) : s);
          g.alpha = k < 0.1 ? k * 10 : Math.min(1, (1 - k) * 8);
          break;
        case "spark":
          g.scale.set(s * (0.5 + k), s * k);
          g.alpha = k;
          break;
        default:
          g.scale.set(s * (0.4 + 0.6 * k), s * (0.4 + 0.6 * k));
          g.alpha = Math.min(1, k * 2.5);
      }
    }
  }

  override destroy(options?: Parameters<Container["destroy"]>[0]): void {
    super.destroy(options ?? { children: true });
    for (const c of this.contexts.values()) c.destroy();
    this.contexts.clear();
  }
}
